import { css } from '@emotion/react'
import { Card } from 'plume-ui'
import { isIPO, isPrivate } from '@/lib/company'
import {
  AddressHistoryData,
  CompanyData,
  ProgramHistoryData,
  TradeNameHistoryData,
  SupporterData,
  SupportProgramData,
} from '../../../../../types/api'
import { CardInnerTitle } from '../../../../atoms/CardInnerTitle'
import { Table, TableCell, TableHeading } from '../../../../atoms/Table'
import { EmptyCard } from '../../../../common/EmptyCard'
import { LineClampText } from '../../../../atoms/LineClampText'
import { A } from '../../../../atoms/A'
import { FormattedDate } from '../../../../atoms/FormattedDate'

type Props = {
  company: CompanyData
  className?: string
}

export const CompanyOtherInfos: React.FC<Props> = (props) => {
  return (
    <>
      {props.company.tradeNameHistories && (
        <TradeNameHistoriesTable
          tradeNameHistories={props.company.tradeNameHistories}
        />
      )}
      {props.company.addressHistories && (
        <div
          css={css`
            margin-top: 2rem;
          `}
        >
          <AddressHistoriesTable
            addressHistories={props.company.addressHistories}
          />
        </div>
      )}
      {isPrivate(props.company) && props.company.programHistories && (
        <div
          css={css`
            margin-top: 2rem;
          `}
        >
          <ProgramHistoriesTable
            programHistories={props.company.programHistories}
          />
        </div>
      )}
      {props.company.supporters && (
        <div
          css={css`
            margin-top: 2rem;
          `}
        >
          <SupportersTable supporters={props.company.supporters} />
        </div>
      )}
      {props.company.supportPrograms && (
        <div
          css={css`
            margin-top: 2rem;
          `}
        >
          <SupportProgramsTable programs={props.company.supportPrograms} />
        </div>
      )}
      {isIPO(props.company) && props.company.listingHistory && (
        <div
          css={css`
            margin-top: 2rem;
          `}
        >
          <Card>
            <CardInnerTitle title="上場履歴" />
            <Card.Content
              css={css`
                white-space: pre-wrap;
                line-height: 1.5;
              `}
            >
              {props.company.listingHistory}
            </Card.Content>
          </Card>
        </div>
      )}
      {props.company.remarks && (
        <div
          css={css`
            margin-top: 2rem;
          `}
        >
          <Card>
            <CardInnerTitle title="備考" />
            <Card.Content
              css={css`
                white-space: pre-wrap;
                line-height: 1.5;
              `}
            >
              {props.company.remarks}
            </Card.Content>
          </Card>
        </div>
      )}
    </>
  )
}

const TradeNameHistoriesTable: React.FC<{
  tradeNameHistories: TradeNameHistoryData[]
}> = (props) => {
  return (
    <Card>
      <CardInnerTitle title="商号履歴" />
      {props.tradeNameHistories.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <TableHeading>変更年月</TableHeading>
              <TableHeading>商号</TableHeading>
            </tr>
          </thead>
          <tbody>
            {props.tradeNameHistories.map((h, i) => (
              <tr key={i}>
                <TableCell>{h.date}</TableCell>
                <TableCell>{h.content}</TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard />
      )}
    </Card>
  )
}

const AddressHistoriesTable: React.FC<{
  addressHistories: AddressHistoryData[]
}> = (props) => {
  return (
    <Card id="address-histories">
      <CardInnerTitle
        title="登記住所変更履歴"
        helpText="2015/10/5以降のデータのみ掲載"
      />
      {props.addressHistories.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <TableHeading>変更年月日</TableHeading>
              <TableHeading>郵便番号</TableHeading>
              <TableHeading>住所</TableHeading>
            </tr>
          </thead>
          <tbody>
            {props.addressHistories.map((h, i) => (
              <tr key={i}>
                <TableCell empty={!h.changeDate}>
                  <FormattedDate>{h.changeDate}</FormattedDate>
                </TableCell>
                <TableCell>{h.postCode}</TableCell>
                <TableCell>{h.address}</TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard />
      )}
    </Card>
  )
}

const ProgramHistoriesTable: React.FC<{
  programHistories: ProgramHistoryData[]
}> = (props) => {
  return (
    <Card>
      <CardInnerTitle title="受賞歴" />
      {props.programHistories.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <TableHeading>受賞年月</TableHeading>
              <TableHeading>受賞内容</TableHeading>
            </tr>
          </thead>
          <tbody>
            {props.programHistories.map((h, i) => (
              <tr key={i}>
                <TableCell empty={!h.date}>{h.date}</TableCell>
                <TableCell>{h.content}</TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard />
      )}
    </Card>
  )
}

const SupportersTable: React.FC<{
  supporters: SupporterData[]
}> = (props) => {
  return (
    <Card>
      <CardInnerTitle title="支援元" />
      {props.supporters.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <TableHeading>ベンチャー支援</TableHeading>
              <TableHeading>種別</TableHeading>
            </tr>
          </thead>
          <tbody>
            {props.supporters.map((s, i) => (
              <tr key={i}>
                <TableCell>
                  <A href={s.path} decoration="none">
                    {s.name}
                  </A>
                </TableCell>
                <TableCell>{s.category}</TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard />
      )}
    </Card>
  )
}

const SupportProgramsTable: React.FC<{
  programs: SupportProgramData[]
}> = (props) => {
  return (
    <Card>
      <CardInnerTitle title="支援プログラム" />
      {props.programs.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <TableHeading>タイトル</TableHeading>
              <TableHeading>カテゴリ</TableHeading>
              <TableHeading>説明</TableHeading>
            </tr>
          </thead>
          <tbody>
            {props.programs.map((p, i) => (
              <tr key={i}>
                <TableCell>
                  <A href={p.path} decoration="none">
                    {p.title}
                  </A>
                </TableCell>
                <TableCell>{p.category}</TableCell>
                <TableCell>
                  <LineClampText lineNumber={3}>{p.description}</LineClampText>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard />
      )}
    </Card>
  )
}
